import { createBrowserRouter } from 'react-router';
import { LoaderFunctionArgs } from 'react-router';

import { checkUserIsConnectedOnStart } from '@/domain/auth/actions/check-user-connected-on-start.action';
import { runImpactCalculateFestivalCo2ForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-co2-for-project.action';
import { runImpactCalculateFestivalEnvForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-env-for-project.action';
import { runImpactCalculateFestivalEssForProject } from '@/domain/impact-calculate-festival/actions/run-impact-calculate-festival-ess-for-project.action';
import { getImpactCalculateTourForProject } from '@/domain/impact-calculate-tour/actions/get-impact-calculate-tour-for-project.action';
import { getImpactCalculateVenueCo2ForProject } from '@/domain/impact-calculate-venue/actions/get-impact-calculate-venue-co2-for-project.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { getImpactEnvQuestions } from '@/domain/impact-env/actions/get-impact-env-questions.action';
import { getImpactEnvResponses } from '@/domain/impact-env/actions/get-impact-env-responses.action';
import { getImpactEssQuestions } from '@/domain/impact-ess/actions/get-impact-ess-questions.action';
import { getImpactEssResponses } from '@/domain/impact-ess/actions/get-impact-ess-responses.action';
import { getImpactReferences } from '@/domain/impact-references/actions/get-impact-references.action';
import { ExportType } from '@/domain/pdf-reports/pdf-reports.type';
import { getProjectFestival } from '@/domain/project-festival/actions/get-project-festival.action';
import { getProjectTour } from '@/domain/project-tour/actions/get-project-tour.action';
import { getTourSequences } from '@/domain/project-tour-ontour/actions/get-tour-sequences.action';
import { getProjectVenue } from '@/domain/project-venue/actions/get-project-venue.action';
import { getVenueDates } from '@/domain/project-venue-dates/actions/get-venue-dates.action';
import { getVenuePresets } from '@/domain/project-venue-presets/actions/get-venue-presets.action';
import { store } from '@/domain/store';
import { Error } from '@/ui/pages/error/Error';

import { PdfProjectFestival } from './PdfProjectFestival';
import { PdfProjectTour } from './PdfProjectTour';
import { PdfProjectVenue } from './PdfProjectVenue';

export const pdfRouter = createBrowserRouter([
  {
    path: '/pdf',
    errorElement: <Error />,
    loader: assertUserIsConnected,
    children: [
      {
        path: 'projects/festival/:id',
        element: <PdfProjectFestival />,
        loader: pdfFestivalLoader,
      },
      {
        path: 'projects/venue/:id',
        element: <PdfProjectVenue />,
        loader: pdfVenueLoader,
      },
      {
        path: 'projects/tour/:id',
        element: <PdfProjectTour />,
        loader: pdfTourLoader,
      },
    ],
  },
]);

export async function assertUserIsConnected() {
  await store.dispatch(checkUserIsConnectedOnStart()).unwrap();
  return null;
}

export async function pdfFestivalLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const projectId = parseInt(params.id as string);
  const [, searchParams] = request.url.split('?');
  const exportType = searchParams
    ? new URLSearchParams(searchParams).get('export_type')
    : null;
  await store.dispatch(getProjectFestival(projectId));
  await store.dispatch(getImpactReferences({ projectId }));
  await store.dispatch(runImpactCalculateFestivalCo2ForProject({ projectId }));
  await store.dispatch(runImpactCalculateFestivalEnvForProject({ projectId }));
  await store.dispatch(runImpactCalculateFestivalEssForProject({ projectId }));
  if (exportType === ExportType.FULL_ANSWER_AND_RESULTS) {
    await store.dispatch(getImpactCo2Answers({ projectId }));
    await store.dispatch(getImpactEssQuestions({}));
    await store.dispatch(getImpactEnvQuestions());
    await store.dispatch(getImpactEssResponses({ projectId }));
    await store.dispatch(getImpactEnvResponses({ projectId }));
  }
  return null;
}

export async function pdfVenueLoader({ params, request }: LoaderFunctionArgs) {
  const projectId = parseInt(params.id as string);
  const [, searchParams] = request.url.split('?');
  const exportType = searchParams
    ? new URLSearchParams(searchParams).get('export_type')
    : null;
  await store.dispatch(getProjectVenue(projectId));
  await store.dispatch(getVenueDates());
  await store.dispatch(getVenuePresets());
  await store.dispatch(getImpactReferences({ projectId }));
  await Promise.all([
    store.dispatch(getImpactCalculateVenueCo2ForProject({ projectId })),
    // Hack to get the ESS_3 question for the PDF page 4.
    store.dispatch(getImpactEssResponses({ projectId })),
  ]);
  if (exportType === ExportType.FULL_ANSWER_AND_RESULTS) {
    await store.dispatch(getImpactCo2Answers({ projectId }));
    await store.dispatch(getImpactEssQuestions({}));
    await store.dispatch(getImpactEnvQuestions());
    await store.dispatch(getImpactEssResponses({ projectId }));
    await store.dispatch(getImpactEnvResponses({ projectId }));
  }
  return null;
}

export async function pdfTourLoader({ params }: LoaderFunctionArgs) {
  const projectId = parseInt(params.id as string);
  await store.dispatch(getProjectTour(projectId));
  await store.dispatch(getTourSequences());
  await store.dispatch(getImpactCalculateTourForProject({ projectId }));
  return null;
}
